import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { PageTransition } from '@/shared/ui/PageTransition'
import {
  registerNativeBackHandler,
  shouldRegisterNativeBackHandler,
} from '@/app/nativeBackHandler'
import { NAV_HIERARCHICAL_BACK_V1 } from '@/shared/config/auditFeatureFlags'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { isMainScrollAtTop } from '@/shared/lib/scrollContainer'
import { useScrollRestoration } from '@/shared/lib/useScrollRestoration'
import { useSessionStore } from '@/features/session/model/sessionStore'
import { useMainDataRefresh } from '@/shared/lib/useMainDataRefresh'
import { cn } from '@/shared/lib/cn'
import { InfoIcon } from '@/shared/ui/icons'
import { Tooltip } from '@/shared/ui/Tooltip'
import { SkipToContentLink } from '@/shared/ui/SkipToContentLink'

const DOC_SECTION_IDS = [
  'pacientes', 'prontuario-exames', 'novo-exame', 'novo-atendimento', 'novo-documento',
  'agenda', 'documentos', 'chat-ia', 'calculadora-doses', 'ultimos-resultados', 'cadastros',
  'tipos-exame', 'locais', 'tipos-atendimento', 'codigos-cid', 'principios-ativos',
  'codigos-procedimentos', 'google-calendar', 'notificacoes', 'inicio', 'configuracoes',
  'meu-perfil', 'certificado-digital', 'importacao', 'prompts-ia', 'usuarios-licencas',
] as const

/** Mapeia pathname (e search quando na doc) para id da seção da documentação. Retorna 'inicio' se não encontrar. */
const pathnameToDocSection = (pathname: string, search?: string): string => {
  if (pathname.startsWith('/documentation') && search) {
    const params = new URLSearchParams(search)
    const section = params.get('section')
    if (section && DOC_SECTION_IDS.includes(section as (typeof DOC_SECTION_IDS)[number])) {
      return section
    }
  }
  const id = (() => {
    if (pathname === '/' || pathname === '/dashboard') return 'inicio'
    if (pathname.startsWith('/patients/')) return 'prontuario-exames'
    if (pathname === '/patients' || pathname.startsWith('/patients')) return 'pacientes'
    if (pathname.startsWith('/documentos')) return 'documentos'
    if (pathname.startsWith('/calendar')) return 'agenda'
    if (pathname.startsWith('/last-results')) return 'ultimos-resultados'
    if (pathname.startsWith('/settings/profile')) return 'meu-perfil'
    if (pathname.startsWith('/doctor/settings/certificate')) return 'certificado-digital'
    if (pathname.startsWith('/settings/certificate')) return 'certificado-digital'
    if (pathname.startsWith('/doctor/settings/local-ai')) return 'configuracoes'
    if (pathname.startsWith('/settings/local-ai')) return 'configuracoes'
    if (pathname.startsWith('/settings/admin-users')) return 'usuarios-licencas'
    if (pathname.startsWith('/settings/import/ai-prompts')) return 'prompts-ia'
    if (pathname.startsWith('/settings/import')) return 'importacao'
    if (pathname.startsWith('/settings/cadastros/exam-types')) return 'tipos-exame'
    if (pathname.startsWith('/settings/cadastros/locations')) return 'locais'
    if (pathname.startsWith('/settings/cadastros/attendance-types')) return 'tipos-atendimento'
    if (pathname.startsWith('/settings/cadastros/active-ingredients')) return 'principios-ativos'
    if (pathname.startsWith('/settings/cadastros/cid-codes')) return 'codigos-cid'
    if (pathname.startsWith('/settings/cadastros/procedure-codes')) return 'codigos-procedimentos'
    if (pathname.startsWith('/settings/cadastros')) return 'cadastros'
    if (pathname.startsWith('/settings/google-calendar')) return 'google-calendar'
    if (pathname.startsWith('/settings')) return 'configuracoes'
    if (pathname.startsWith('/add-exam')) return 'novo-exame'
    if (pathname.startsWith('/add-encounter')) return 'novo-atendimento'
    if (pathname.startsWith('/chat-ia')) return 'chat-ia'
    if (pathname.startsWith('/calculator')) return 'calculadora-doses'
    return 'inicio'
  })()
  return DOC_SECTION_IDS.includes(id as (typeof DOC_SECTION_IDS)[number]) ? id : 'inicio'
}
import { BottomNavigation } from '@/widgets/navigation/BottomNavigation'
import { MobileMenuSheet } from '@/widgets/navigation/MobileMenuSheet'
import { SidebarNavigation } from '@/widgets/navigation/SidebarNavigation'
import { GoToPagePalette } from '@/widgets/navigation/GoToPagePalette'
import { useGoToPageHotkey } from '@/widgets/navigation/useGoToPageHotkey'
import { UnsavedChangesProvider } from '@/shared/ui/UnsavedChangesProvider'
import { SaveFABProvider } from '@/shared/ui/SaveFABProvider'
import { useSaveFABContext } from '@/shared/ui/SaveFABContext'
import { NetworkStatusToast } from '@/shared/ui/NetworkStatusToast'
import { OutboxSyncIndicator } from '@/shared/ui/OutboxSyncIndicator'
import { ForegroundMessageHandler } from '@/features/push/ForegroundMessageHandler'
import { NotificationFromPushHandler } from '@/features/notifications/NotificationFromPushHandler'
import { BellMenu } from '@/features/notifications/ui/BellMenu'
import { AccountMenu } from '@/widgets/navigation/AccountMenu'
import { DesktopTopBarDictate, DesktopTopBarNewDocument } from '@/widgets/navigation/DesktopTopBar'
import { GoToSearchTrigger } from '@/widgets/navigation/GoToSearchTrigger'

const resolveTabletSectionTitle = (pathname: string, userName: string): string => {
  if (pathname === '/' || pathname === '/dashboard') {
    return userName ? `Olá, ${userName}.` : 'Olá.'
  }
  if (pathname.startsWith('/patients/')) return 'Prontuário'
  if (pathname.startsWith('/patients')) return 'Pacientes'
  if (pathname.startsWith('/documentos')) return 'Documentos'
  if (pathname.startsWith('/calendar')) return 'Agenda'
  if (pathname.startsWith('/last-results')) return 'Últimos Resultados'
  if (pathname.startsWith('/documentation')) return 'Documentação'
  if (pathname.startsWith('/settings/admin-users')) return 'Usuários & Licença'
  if (pathname.startsWith('/settings/import/document-models')) return 'Importar modelos de documento'
  if (pathname.startsWith('/settings/import/exams')) return 'Importar Exames'
  if (pathname.startsWith('/settings/import/patients')) return 'Importar Pacientes'
  if (pathname.startsWith('/settings/import/ai-prompts')) return 'Textos do assistente'
  if (pathname.startsWith('/settings/cadastros/exam-types/edit')) return 'Editar Tipo de Exame'
  if (pathname.startsWith('/settings/cadastros/exam-types')) return 'Tipos de Exame'
  if (pathname.startsWith('/settings/cadastros/locations/edit')) return 'Editar Local'
  if (pathname.startsWith('/settings/cadastros/locations')) return 'Locais'
  if (pathname.startsWith('/settings/cadastros/attendance-types/edit')) return 'Editar Tipo de Atendimento'
  if (pathname.startsWith('/settings/cadastros/attendance-types')) return 'Tipos de Atendimento'
  if (pathname.startsWith('/settings/cadastros/active-ingredients/edit')) return 'Editar Princípio Ativo'
  if (pathname.startsWith('/settings/cadastros/active-ingredients')) return 'Princípios Ativos'
  if (pathname.startsWith('/settings/cadastros/cid-codes/edit')) return 'Editar Código CID'
  if (pathname.startsWith('/settings/cadastros/cid-codes')) return 'Códigos CID-10'
  if (pathname.startsWith('/settings/import/cid')) return 'Importar códigos CID-10'
  if (pathname.startsWith('/settings/cadastros/procedure-codes/edit')) return 'Editar Código TUSS/CBHPM'
  if (pathname.startsWith('/settings/cadastros/procedure-codes')) return 'Códigos TUSS e CBHPM'
  if (pathname.startsWith('/settings/import/procedure-codes')) return 'Importar códigos TUSS e CBHPM'
  if (pathname.startsWith('/settings/cadastros')) return 'Cadastros'
  if (pathname.startsWith('/settings/import')) return 'Central de Integrações de Dados'
  if (pathname.startsWith('/settings/google-calendar')) return 'Google Calendar'
  if (pathname.startsWith('/settings/feed')) return 'Feed de notícias'
  if (pathname.startsWith('/settings/open-source')) return 'Software livre e atribuições'
  if (pathname.startsWith('/settings/profile')) return 'Meu perfil'
  if (pathname.startsWith('/doctor/settings/certificate')) return 'Assinatura digital'
  if (pathname.startsWith('/settings/certificate')) return 'Assinatura digital'
  if (pathname.startsWith('/doctor/settings/local-ai')) return 'IA no Dispositivo'
  if (pathname.startsWith('/settings/local-ai')) return 'IA no Dispositivo'
  if (pathname.startsWith('/settings')) return 'Configurações'
  if (pathname.startsWith('/add-exam')) return 'Novo Exame'
  if (pathname.startsWith('/add-encounter')) return 'Novo Atendimento'
  if (pathname.startsWith('/chat-ia')) return 'Fale com Synap...'
  if (pathname === '/anatomy' || pathname.startsWith('/anatomy/')) return 'Atlas Humano'
  if (pathname.startsWith('/calculator')) return 'Doses Pré-calculadas'
  if (pathname === '/inventory/new') return 'Novo lançamento'
  if (/^\/inventory\/[^/]+\/edit$/.test(pathname)) return 'Ajustar estoque'
  if (pathname.startsWith('/inventory')) return 'Estoque'
  if (pathname.startsWith('/feedback')) return 'Enviar feedback'
  if (pathname.startsWith('/notifications')) return 'Notificações'
  return 'Início'
}

export const AppShell = () => {
  const location = useLocation()
  const userName = useSessionStore((state) => state.user?.name?.trim() || '')
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const tabletSectionTitle = useMemo(
    () => resolveTabletSectionTitle(location.pathname, userName),
    [location.pathname, userName],
  )

  // /anatomy é full-bleed: a barra inferior cobriria o canvas e o gatilho do sheet de sistemas.
  const hideMobileNavigation =
    location.pathname === '/login' ||
    location.pathname === '/anatomy' ||
    location.pathname.startsWith('/anatomy/')

  return (
    <UnsavedChangesProvider>
      <SaveFABProvider>
        <AppShellContent
            location={location}
            tabletSectionTitle={tabletSectionTitle}
            sidebarCollapsed={sidebarCollapsed}
            setSidebarCollapsed={setSidebarCollapsed}
            hideMobileNavigation={hideMobileNavigation}
          />
        </SaveFABProvider>
      </UnsavedChangesProvider>
  )
}

const MAX_PULL_DISTANCE = 116
const TRIGGER_DISTANCE = 72

function AppShellContent({
  location,
  tabletSectionTitle,
  sidebarCollapsed,
  setSidebarCollapsed,
  hideMobileNavigation,
}: {
  location: { pathname: string; search?: string }
  tabletSectionTitle: string
  sidebarCollapsed: boolean
  setSidebarCollapsed: React.Dispatch<React.SetStateAction<boolean>>
  hideMobileNavigation: boolean
}) {
  const navigate = useNavigate()
  const { hasSaveFAB, needsFloatingPadding } = useSaveFABContext()
  const isDashboard = location.pathname === '/' || location.pathname === '/dashboard'
  const isImmersiveRoute =
    location.pathname.startsWith('/chat-ia') ||
    location.pathname === '/anatomy' ||
    location.pathname.startsWith('/anatomy/')
  const user = useSessionStore((state) => state.user)
  const userName = user?.name?.trim() || ''
  const { refresh: mainRefresh, refreshing: mainRefreshing } = useMainDataRefresh()
  const [goToOpen, setGoToOpen] = useState(false)
  const openGoTo = useCallback(() => setGoToOpen(true), [])
  useGoToPageHotkey(openGoTo)

  useScrollRestoration()

  useEffect(() => {
    if (!shouldRegisterNativeBackHandler(NAV_HIERARCHICAL_BACK_V1)) return
    return registerNativeBackHandler({
      navigate,
      getPathname: () => location.pathname,
    })
  }, [navigate, location.pathname])

  // Pull-to-refresh no header
  const [pullDistance, setPullDistance] = useState(0)
  const [readyToRefresh, setReadyToRefresh] = useState(false)
  const [coarsePointer, setCoarsePointer] = useState(false)
  const startYRef = useRef<number | null>(null)
  const headerRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const media = window.matchMedia('(pointer: coarse)')
    const sync = () => setCoarsePointer(media.matches)
    sync()
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', sync)
      return () => media.removeEventListener('change', sync)
    }
    media.addListener(sync)
    return () => media.removeListener(sync)
  }, [])

  useEffect(() => {
    const el = headerRef.current
    if (!el || !coarsePointer || !isDashboard) return
    const onMove = (ev: globalThis.TouchEvent) => {
      if (mainRefreshing || startYRef.current === null) return
      const currentY = ev.touches[0]?.clientY ?? startYRef.current
      const delta = currentY - startYRef.current
      if (delta <= 0) {
        setPullDistance(0)
        setReadyToRefresh(false)
        return
      }
      const dampened = Math.min(MAX_PULL_DISTANCE, delta * 0.45)
      setPullDistance(dampened)
      setReadyToRefresh(dampened >= TRIGGER_DISTANCE)
      ev.preventDefault()
    }
    el.addEventListener('touchmove', onMove, { passive: false })
    return () => el.removeEventListener('touchmove', onMove)
  }, [coarsePointer, isDashboard, mainRefreshing])

  const handleHeaderTouchStart = (event: React.TouchEvent) => {
    if (!isDashboard || mainRefreshing || !coarsePointer) return
    if (!isMainScrollAtTop()) return
    startYRef.current = event.touches[0]?.clientY ?? null
  }

  const handleHeaderTouchEnd = useCallback(async () => {
    startYRef.current = null
    if (mainRefreshing) {
      setPullDistance(0)
      setReadyToRefresh(false)
      return
    }
    const should = readyToRefresh
    setPullDistance(0)
    setReadyToRefresh(false)
    if (!should) return
    await mainRefresh()
  }, [mainRefresh, mainRefreshing, readyToRefresh])

  const showIndicator = mainRefreshing || pullDistance > 0
  const pullProgress = Math.min(1, pullDistance / TRIGGER_DISTANCE)
  const indicatorLabel = mainRefreshing
    ? 'Atualizando dados...'
    : readyToRefresh
      ? 'Solte para atualizar'
      : 'Puxe para atualizar'

  return (
    <div className="flex h-dvh w-full flex-col overflow-hidden bg-[var(--color-page)]">
      <SkipToContentLink />
      {/* A11Y-037: NetworkStatusToast z-50; header z-20; toasts de sistema usam Escape via useDialogA11y nos overlays. */}
      <NetworkStatusToast />
      <OutboxSyncIndicator />
      <ForegroundMessageHandler />
      <NotificationFromPushHandler />
      {/* Cabeçalho fixo no topo da viewport em desktop/tablet */}
      <header
        ref={headerRef}
        onTouchStart={isDashboard ? handleHeaderTouchStart : undefined}
        onTouchEnd={isDashboard ? () => { void handleHeaderTouchEnd() } : undefined}
        onTouchCancel={isDashboard ? () => { void handleHeaderTouchEnd() } : undefined}
        className="relative z-20 flex min-h-[36px] shrink-0 items-center justify-between gap-2 bg-[var(--color-chrome)] px-4 py-1 md:fixed md:left-0 md:right-0 md:top-0 md:px-6 lg:px-8"
      >
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {userName ? (
            <div className="md:hidden">
              <AccountMenu
                variant="header"
                name={userName}
                email={user?.email}
                avatarUrl={user?.avatarUrl}
                role={user?.role ?? 'doctor'}
                isSystemAdmin={user?.isSystemAdmin ?? false}
              />
            </div>
          ) : null}
          <GoToSearchTrigger variant="headerIcon" onOpen={openGoTo} />
          <h1 className="min-w-0 flex-1 truncate text-base font-semibold text-[var(--color-chrome-foreground)]">
            {tabletSectionTitle}
          </h1>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <DesktopTopBarDictate />
          <BellMenu />
          <Tooltip label="Documentação">
            <Link
              to={`/documentation?section=${pathnameToDocSection(location.pathname, location.search)}`}
              className="inline-flex h-11 w-11 min-h-[44px] min-w-[44px] items-center justify-center rounded-lg text-[var(--color-chrome-foreground)] transition-colors hover:bg-[var(--color-chrome-hover)]"
              aria-label="Abrir documentação"
            >
              <InfoIcon size={22} className="text-[var(--color-chrome-foreground)]" />
            </Link>
          </Tooltip>
          <DesktopTopBarNewDocument />
        </div>
        {isDashboard && showIndicator ? (
          <div className="pointer-events-none absolute left-0 right-0 top-full z-30 flex justify-center pt-1">
            <div
              className="pointer-events-none inline-flex min-h-8 items-center rounded-full border border-[var(--color-border-strong)] bg-white/95 px-3 text-[11px] font-semibold text-[var(--color-text-secondary)] shadow-[0_8px_20px_rgba(31,43,40,0.08)] transition-all duration-200"
              style={{ transform: `translateY(0) scale(${0.96 + pullProgress * 0.04})` }}
              aria-live="polite"
            >
              {indicatorLabel}
            </div>
          </div>
        ) : null}
      </header>
      {/* Desktop/tablet: sidebar fixa à esquerda (colada no cabeçalho verde); main com margin e scroll independente */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden pt-0 md:pt-[calc(36px+env(safe-area-inset-top,0px))]">
        <SidebarNavigation
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed((current) => !current)}
          onOpenGoTo={openGoTo}
        />
        <GoToPagePalette open={goToOpen} onClose={() => setGoToOpen(false)} />
        <main
          id="synap-main-content"
          data-scroll-container
          className={cn(
            'min-w-0 flex-1',
            isImmersiveRoute
              ? 'flex flex-col overflow-hidden overscroll-none p-0'
              : cn(
                  'overflow-x-hidden overflow-y-auto overscroll-y-contain px-4 pt-4 md:px-6 md:pt-5 lg:px-8 2xl:px-10',
                  hasSaveFAB && needsFloatingPadding ? 'pb-48 md:pb-28' : 'pb-24 md:pb-8',
                ),
            sidebarCollapsed ? 'md:ml-[92px]' : 'md:ml-[248px]',
          )}
        >
          <PageTransition transitionKey={location.pathname} className={isImmersiveRoute ? 'flex min-h-0 flex-1 flex-col' : undefined}>
            <Outlet />
          </PageTransition>
        </main>
      </div>
      <BottomNavigation hidden={hideMobileNavigation} />
      <MobileMenuSheet />
    </div>
  )
}

